import { useState, useEffect } from "react";
import axios from "axios";
import { Lock, Plus, Edit, Trash2, LogOut, Package, ShoppingBag, Eye, EyeOff, Check, X, Megaphone, Upload, ArrowUp, ArrowDown, Settings, FileText, Sun } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "dontello_admin_token";

function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Error";
}

// Convierte una URL relativa tipo /api/assets/xxx a absoluta (solo para preview HTML)
function assetUrl(url) {
  if (!url) return url;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${BACKEND_URL}${url}`;
}

// ========== FILE UPLOAD ==========
function FileUpload({ value, onChange, accept = "image/*,video/mp4", label = "Subir archivo" }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2 flex-wrap items-center">
        <label className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg font-semibold text-sm cursor-pointer hover:bg-slate-700">
          <Upload className="w-4 h-4" />
          {uploading ? "Subiendo..." : label}
          <input type="file" accept={accept} onChange={handleFile} className="hidden" disabled={uploading} />
        </label>
        <span className="text-xs text-slate-500">o pega una URL debajo</span>
      </div>
      <input type="url" value={value || ""} onChange={(e) => onChange(e.target.value)}
        placeholder="https://... o /api/assets/..."
        className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
      {error && <div className="text-xs text-red-600">{error}</div>}
      {value && (
        <div className="mt-2 rounded-lg border bg-slate-50 p-2">
          {accept && accept.includes("pdf") ? (
            <a href={assetUrl(value)} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 text-sm text-green-700 hover:text-green-900 font-semibold">
              <FileText className="w-4 h-4" />
              Ver PDF subido →
            </a>
          ) : (
            <img src={assetUrl(value)} alt="preview" className="max-h-40 rounded"
              onError={(e) => { e.target.style.display = "none"; }} />
          )}
        </div>
      )}
    </div>
  );
}

// ========== LOGIN ==========
function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.email);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-green-900 flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-3">
            <Lock className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-black text-slate-900">Panel Administrador</h1>
          <p className="text-slate-500 text-sm mt-1">Don Tello Pádel y Tenis</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              data-testid="admin-login-email"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-green-600" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Contraseña</label>
            <div className="relative">
              <input type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                data-testid="admin-login-password"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:border-green-600" />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg" data-testid="admin-login-error">{error}</div>}
          <button type="submit" disabled={loading} data-testid="admin-login-submit"
            className="w-full bg-green-600 text-white py-3 rounded-full font-bold hover:bg-green-700 disabled:opacity-50 transition-colors">
            {loading ? "Entrando..." : "Entrar"}
          </button>
        </form>
        <p className="text-center text-xs text-slate-400 mt-6">
          <a href="/" className="hover:text-green-600">← Volver a la web</a>
        </p>
      </div>
    </div>
  );
}

// ========== PRODUCT FORM ==========
function ProductForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    name: "",
    description: "",
    price: "",
    image: "",
    category: "palas",
    stock: 1,
    url: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isEdit = !!initial?.id;

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        stock: form.stock === "" || form.stock === null ? null : parseInt(form.stock, 10),
      };
      if (isEdit) {
        await axios.patch(`${API}/admin/products/${initial.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers: authHeaders() });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="product-form-modal">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold">{isEdit ? "Editar producto" : "Nueva pala / producto"}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Nombre *</label>
            <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="pf-name"
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-green-600" />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Descripción *</label>
            <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows="3" data-testid="pf-description"
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-green-600" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-1">Precio (€) *</label>
              <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} data-testid="pf-price"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-green-600" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Stock</label>
              <input type="number" min="0" value={form.stock ?? ""} onChange={(e) => setForm({...form, stock: e.target.value})} data-testid="pf-stock"
                placeholder="Vacío = ilimitado"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-green-600" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Categoría</label>
              <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="pf-category"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-green-600">
                <option value="palas">Palas</option>
                <option value="clases">Clases Particulares</option>
                <option value="equipacion">Equipación</option>
                <option value="pelotas">Pelotas</option>
                <option value="overgrips">Overgrips</option>
                <option value="bonos">Bonos Playtomic</option>
                <option value="reparacion">Reparación</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Imagen del producto *</label>
            <FileUpload
              value={form.image}
              onChange={(url) => setForm({...form, image: url})}
              accept="image/*"
              label="Subir foto"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">URL Wallapop (opcional)</label>
            <input type="url" value={form.url || ""} onChange={(e) => setForm({...form, url: e.target.value})} data-testid="pf-url"
              placeholder="https://es.wallapop.com/item/..."
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-green-600" />
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">{error}</div>}
        </div>
        <div className="p-6 border-t flex gap-3 justify-end">
          <button onClick={onClose} className="px-5 py-2 rounded-full bg-slate-100 font-semibold hover:bg-slate-200">Cancelar</button>
          <button onClick={save} disabled={saving || !form.name || !form.price || !form.image} data-testid="pf-save"
            className="px-6 py-2 rounded-full bg-green-600 text-white font-bold hover:bg-green-700 disabled:opacity-50">
            {saving ? "Guardando..." : (isEdit ? "Guardar cambios" : "Crear")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ========== ORDERS LIST ==========
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/orders`, { headers: authHeaders() });
        setOrders(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-8 text-center text-slate-500">Cargando pedidos...</div>;

  if (orders.length === 0) {
    return <div className="p-12 text-center text-slate-500 bg-slate-50 rounded-2xl">Aún no hay pedidos online.</div>;
  }

  const statusBadge = (o) => {
    if (o.payment_status === "paid") return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800">PAGADO</span>;
    if (o.status === "expired") return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-600">EXPIRADO</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800">PENDIENTE</span>;
  };

  return (
    <div className="space-y-3" data-testid="orders-list">
      {orders.map((o, i) => (
        <div key={o.session_id || i} className="bg-white border rounded-xl p-4 text-sm">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="font-bold text-slate-900">{o.customer_name || "-"} · {o.customer_email || "-"}</div>
              <div className="text-slate-500 text-xs mt-0.5">{(o.created_at || "").slice(0, 16).replace("T", " ")} · {o.delivery_method === "shipping" ? "Envío" : "Recogida en club"}</div>
              {o.shipping_address && <div className="text-slate-500 text-xs">📦 {o.shipping_address}</div>}
              {o.metadata?.customer_phone && <div className="text-slate-500 text-xs">📞 {o.metadata.customer_phone}</div>}
              {o.metadata?.customer_nif && <div className="text-slate-500 text-xs">🧾 NIF: {o.metadata.customer_nif}</div>}
              {o.metadata?.playtomic_username && <div className="text-amber-700 text-xs font-semibold">🎾 Playtomic: {o.metadata.playtomic_username}</div>}
              <div className="text-slate-600 text-xs mt-1 italic">{o.metadata?.items_summary}</div>
            </div>
            <div className="text-right">
              <div className="text-lg font-black text-slate-900">{o.amount_total?.toFixed(2)}€</div>
              {statusBadge(o)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ========== PRODUCTS TAB ==========
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/products`, { headers: authHeaders() });
      setProducts(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markSold = async (p) => {
    if (!window.confirm(`¿Marcar "${p.name}" como VENDIDA?`)) return;
    await axios.post(`${API}/admin/products/${p.id}/sold`, {}, { headers: authHeaders() });
    load();
  };

  const restock = async (p) => {
    const n = window.prompt(`Unidades disponibles para "${p.name}":`, "1");
    if (n === null) return;
    const qty = parseInt(n, 10);
    if (isNaN(qty) || qty < 0) return;
    await axios.post(`${API}/admin/products/${p.id}/restock?stock=${qty}`, {}, { headers: authHeaders() });
    load();
  };

  const del = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}" PERMANENTEMENTE? Esta acción no se puede deshacer.`)) return;
    await axios.delete(`${API}/admin/products/${p.id}`, { headers: authHeaders() });
    load();
  };

  const cats = ["all", "bonos", "clases", "palas", "equipacion", "pelotas", "overgrips", "reparacion"];
  const filtered = filter === "all" ? products : products.filter((p) => p.category === filter);

  return (
    <div data-testid="products-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-2">
          {cats.map((c) => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase ${filter === c ? "bg-green-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
              {c === "all" ? "Todos" : c}
            </button>
          ))}
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-product-btn"
          className="inline-flex items-center gap-2 bg-green-600 text-white px-5 py-2 rounded-full font-bold hover:bg-green-700">
          <Plus className="w-4 h-4" /> Añadir pala
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-500">Cargando...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((p) => (
            <div key={p.id} data-testid={`admin-product-${p.id}`} className="bg-white border rounded-xl overflow-hidden flex flex-col">
              <div className="relative aspect-video bg-slate-100">
                <img src={assetUrl(p.image)} alt={p.name} className="w-full h-full object-contain" onError={(e) => e.target.style.display='none'} />
                {(!p.in_stock || p.stock === 0) && (
                  <div className="absolute top-2 right-2 bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">VENDIDA</div>
                )}
                <div className="absolute bottom-2 left-2 bg-slate-900/80 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">{p.category}</div>
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <h3 className="font-bold text-sm text-slate-900 line-clamp-2 mb-1">{p.name}</h3>
                <div className="text-xs text-slate-500 mb-2">
                  {p.price}€ · Stock: {p.stock === null || p.stock === undefined ? "∞" : p.stock}
                </div>
                <div className="mt-auto flex gap-1 flex-wrap">
                  <button onClick={() => { setEditing(p); setShowForm(true); }} title="Editar"
                    className="flex-1 min-w-[70px] inline-flex items-center justify-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold px-2 py-1.5 rounded-lg">
                    <Edit className="w-3 h-3" /> Editar
                  </button>
                  {p.in_stock && p.stock !== 0 ? (
                    <button onClick={() => markSold(p)} title="Marcar como vendida"
                      className="flex-1 min-w-[70px] inline-flex items-center justify-center gap-1 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 text-xs font-bold px-2 py-1.5 rounded-lg">
                      <Check className="w-3 h-3" /> Vendida
                    </button>
                  ) : (
                    <button onClick={() => restock(p)} title="Reponer stock"
                      className="flex-1 min-w-[70px] inline-flex items-center justify-center gap-1 bg-green-100 hover:bg-green-200 text-green-800 text-xs font-bold px-2 py-1.5 rounded-lg">
                      <Plus className="w-3 h-3" /> Reponer
                    </button>
                  )}
                  <button onClick={() => del(p)} title="Eliminar permanentemente"
                    className="inline-flex items-center justify-center bg-red-100 hover:bg-red-200 text-red-600 text-xs font-bold w-8 h-8 rounded-lg">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && <ProductForm initial={editing} onClose={() => setShowForm(false)} onSaved={load} />}
    </div>
  );
}

// ========== PROMO BANNER TAB ==========
function PromoBannerTab() {
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [previewDate, setPreviewDate] = useState(""); // empty = "now"

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/promo-banner`);
        setBanner({ enabled: !!data.enabled, slides: Array.isArray(data.slides) ? data.slides : [] });
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await axios.put(`${API}/admin/promo-banner`, banner, { headers: authHeaders() });
      setMsg("✅ Banner guardado. Recarga la web para ver los cambios.");
    } catch (e) {
      setMsg("❌ Error: " + errMsg(e));
    } finally { setSaving(false); }
  };

  if (loading || !banner) return <div className="p-8 text-center text-slate-500">Cargando...</div>;

  const setSlide = (i, k, v) => {
    const next = banner.slides.slice();
    next[i] = { ...next[i], [k]: v };
    setBanner({ ...banner, slides: next });
  };
  const addSlide = () => {
    setBanner({
      ...banner,
      slides: [...banner.slides, {
        title: "", subtitle: "", media_url: null, media_type: "image",
        cta_label: "", cta_url: "", background: "green",
      }],
    });
  };
  const removeSlide = (i) => {
    if (!window.confirm("¿Eliminar este slide?")) return;
    setBanner({ ...banner, slides: banner.slides.filter((_, k) => k !== i) });
  };
  const moveSlide = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= banner.slides.length) return;
    const next = banner.slides.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setBanner({ ...banner, slides: next });
  };

  const colors = [
    { val: "green", label: "Verde", cls: "bg-gradient-to-r from-green-600 to-emerald-500" },
    { val: "amber", label: "Ámbar", cls: "bg-gradient-to-r from-amber-500 to-orange-500" },
    { val: "dark", label: "Oscuro", cls: "bg-gradient-to-r from-slate-900 to-slate-700" },
    { val: "red", label: "Rojo", cls: "bg-gradient-to-r from-red-600 to-rose-500" },
  ];

  // Compute schedule status for a slide at a given reference date
  const slideStatus = (s, refDate) => {
    const ref = refDate || new Date();
    if (s.starts_at && new Date(s.starts_at) > ref) return { label: "Programado", cls: "bg-blue-100 text-blue-700", active: false };
    if (s.ends_at && new Date(s.ends_at) < ref) return { label: "Caducado", cls: "bg-slate-200 text-slate-600", active: false };
    if (s.starts_at || s.ends_at) return { label: "Activo", cls: "bg-green-100 text-green-700", active: true };
    return { label: "Siempre visible", cls: "bg-slate-100 text-slate-600", active: true };
  };

  const refDate = previewDate ? new Date(previewDate) : new Date();
  const activeSlidesAtRef = banner.slides.filter((s) => slideStatus(s, refDate).active);

  return (
    <div className="max-w-4xl" data-testid="promo-banner-tab">
      <div className="bg-white border rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Banner Promocional (Carrusel)</h3>
            <p className="text-xs text-slate-500 mt-1">Aparece entre el hero y los campamentos. Si añades más de un slide, rotan automáticamente cada 6 segundos con flechas y puntos de navegación.</p>
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={banner.enabled} onChange={(e) => setBanner({ ...banner, enabled: e.target.checked })} className="sr-only peer" data-testid="promo-enabled" />
            <div className="relative w-14 h-7 bg-slate-300 peer-checked:bg-green-600 rounded-full transition-colors">
              <div className={`absolute top-0.5 left-0.5 bg-white w-6 h-6 rounded-full transition-transform ${banner.enabled ? "translate-x-7" : ""}`}></div>
            </div>
            <span className="ml-2 text-sm font-bold">{banner.enabled ? "Activo" : "Oculto"}</span>
          </label>
        </div>

        {banner.slides.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg text-slate-500">
            Aún no hay slides. Pulsa "Añadir slide" para crear el primero.
          </div>
        )}

        {banner.slides.length > 0 && (
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-bold text-indigo-900 mb-1">🔮 Vista previa — ¿cómo se verá la web el día...?</label>
                <input
                  type="datetime-local"
                  value={previewDate}
                  onChange={(e) => setPreviewDate(e.target.value)}
                  data-testid="promo-preview-date"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 bg-white"
                />
              </div>
              {previewDate && (
                <button onClick={() => setPreviewDate("")} data-testid="promo-preview-reset"
                  className="text-xs font-bold text-indigo-700 hover:text-indigo-900 underline self-end pb-2">
                  Volver a "Ahora"
                </button>
              )}
            </div>
            <div className="text-sm">
              {previewDate ? (
                <p className="text-indigo-900">
                  El <span className="font-bold">{new Date(previewDate).toLocaleString("es-ES", { dateStyle: "long", timeStyle: "short" })}</span>{" "}
                  {activeSlidesAtRef.length === 0 ? (
                    <span className="font-bold text-red-700">el banner estará OCULTO</span>
                  ) : (
                    <>
                      se mostrarán <span className="font-bold">{activeSlidesAtRef.length} slide{activeSlidesAtRef.length > 1 ? "s" : ""}</span>:
                    </>
                  )}
                </p>
              ) : (
                <p className="text-indigo-900">
                  Ahora mismo {activeSlidesAtRef.length === 0 ? (
                    <span className="font-bold text-red-700">el banner está OCULTO</span>
                  ) : (
                    <>se mostrarán <span className="font-bold">{activeSlidesAtRef.length} slide{activeSlidesAtRef.length > 1 ? "s" : ""}</span>:</>
                  )}
                </p>
              )}
              {activeSlidesAtRef.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {activeSlidesAtRef.map((s, k) => (
                    <li key={k} className="flex items-center gap-2 text-slate-700">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="font-medium">{s.title?.trim() || "(sin título)"}</span>
                    </li>
                  ))}
                </ul>
              )}
              {activeSlidesAtRef.length === 1 && (
                <p className="mt-2 text-xs text-slate-600 italic">Con solo 1 slide activo, se mostrará estático (sin flechas ni puntos).</p>
              )}
              {activeSlidesAtRef.length >= 2 && (
                <p className="mt-2 text-xs text-slate-600 italic">Rotarán automáticamente cada 6 segundos con flechas y puntos de navegación.</p>
              )}
            </div>
          </div>
        )}

        {banner.slides.map((s, i) => (
          <div key={i} className="border-2 border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50/40" data-testid={`promo-slide-${i}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-600 text-white text-xs font-bold">{i + 1}</span>
                <span className="font-bold text-slate-800">{s.title?.trim() || `Slide ${i + 1}`}</span>
                {(() => {
                  const st = slideStatus(s, refDate);
                  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>;
                })()}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => moveSlide(i, -1)} disabled={i === 0} data-testid={`promo-slide-up-${i}`}
                  className="p-1.5 rounded hover:bg-slate-200 disabled:opacity-30" title="Subir">
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button onClick={() => moveSlide(i, 1)} disabled={i === banner.slides.length - 1} data-testid={`promo-slide-down-${i}`}
                  className="p-1.5 rounded hover:bg-slate-200 disabled:opacity-30" title="Bajar">
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button onClick={() => removeSlide(i)} data-testid={`promo-slide-remove-${i}`}
                  className="p-1.5 rounded hover:bg-red-100 text-red-600" title="Eliminar">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">Título</label>
              <input type="text" value={s.title || ""} onChange={(e) => setSlide(i, "title", e.target.value)}
                placeholder="Ej: ¡Torneo de verano! Apúntate ya"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-green-600" />
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">Subtítulo / Descripción</label>
              <textarea value={s.subtitle || ""} onChange={(e) => setSlide(i, "subtitle", e.target.value)} rows="2"
                placeholder="Texto descriptivo de la promoción"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-green-600" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold mb-1">Tipo de media</label>
                <select value={s.media_type || "image"} onChange={(e) => setSlide(i, "media_type", e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-green-600">
                  <option value="image">Imagen</option>
                  <option value="video">Vídeo (MP4)</option>
                  <option value="youtube">YouTube</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold mb-1">
                  {s.media_type === "youtube" ? "URL de YouTube" : (s.media_type === "video" ? "Archivo o URL de vídeo" : "Foto o URL")}
                </label>
                {s.media_type === "youtube" ? (
                  <input type="url" value={s.media_url || ""} onChange={(e) => setSlide(i, "media_url", e.target.value)}
                    placeholder="https://youtu.be/..."
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-green-600" />
                ) : (
                  <FileUpload
                    value={s.media_url}
                    onChange={(url) => setSlide(i, "media_url", url)}
                    accept={s.media_type === "video" ? "video/mp4,video/webm" : "image/*"}
                    label={s.media_type === "video" ? "Subir vídeo" : "Subir foto"}
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold mb-1">Texto botón (opcional)</label>
                <input type="text" value={s.cta_label || ""} onChange={(e) => setSlide(i, "cta_label", e.target.value)}
                  placeholder="Ej: Apuntarme"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-green-600" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">URL botón (opcional)</label>
                <input type="url" value={s.cta_url || ""} onChange={(e) => setSlide(i, "cta_url", e.target.value)}
                  placeholder="https://... o #tienda"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-green-600" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-blue-50/40 border border-blue-100 rounded-lg">
              <div>
                <label className="block text-xs font-semibold mb-1 text-slate-700">📅 Mostrar desde (opcional)</label>
                <input
                  type="datetime-local"
                  value={s.starts_at || ""}
                  onChange={(e) => setSlide(i, "starts_at", e.target.value || null)}
                  data-testid={`promo-slide-starts-${i}`}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-slate-700">📅 Mostrar hasta (opcional)</label>
                <input
                  type="datetime-local"
                  value={s.ends_at || ""}
                  onChange={(e) => setSlide(i, "ends_at", e.target.value || null)}
                  data-testid={`promo-slide-ends-${i}`}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                />
              </div>
              <p className="sm:col-span-2 text-[11px] text-slate-500">
                Deja en blanco para mostrar siempre. El slide aparece automáticamente en la fecha de inicio y desaparece al pasar la de fin.
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">Color de fondo</label>
              <div className="flex flex-wrap gap-2">
                {colors.map((c) => (
                  <button key={c.val} onClick={() => setSlide(i, "background", c.val)}
                    className={`${c.cls} text-white px-4 py-2 rounded-full text-xs font-bold ${s.background === c.val ? "ring-4 ring-offset-2 ring-slate-400" : ""}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}

        <button onClick={addSlide} data-testid="promo-add-slide"
          className="w-full border-2 border-dashed border-green-300 text-green-700 hover:bg-green-50 px-4 py-3 rounded-xl font-bold inline-flex items-center justify-center gap-2 transition-colors">
          <Plus className="w-4 h-4" /> Añadir slide
        </button>

        {msg && <div className={`px-3 py-2 rounded-lg text-sm ${msg.startsWith("✅") ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>{msg}</div>}

        <div className="flex gap-2 pt-2">
          <button onClick={save} disabled={saving} data-testid="promo-save"
            className="bg-green-600 text-white px-6 py-2 rounded-full font-bold hover:bg-green-700 disabled:opacity-50">
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
          <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-600 hover:text-green-600 px-3 py-2">Ver en la web →</a>
        </div>
      </div>
    </div>
  );
}

// ========== SITE CONTENT TAB ==========
function SiteContentTab() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/site-content`);
        setContent({
          campamentos_enabled: !!data.campamentos_enabled,
          services_images: data.services_images || {},
          pricing_pdf_url: data.pricing_pdf_url || "",
        });
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await axios.put(`${API}/admin/site-content`, content, { headers: authHeaders() });
      setMsg("✅ Cambios guardados. Recarga la web para verlos.");
    } catch (e) {
      setMsg("❌ Error: " + errMsg(e));
    } finally { setSaving(false); }
  };

  if (loading || !content) return <div className="p-8 text-center text-slate-500">Cargando...</div>;

  const servicesList = [
    { id: "clases", label: "Clases de Pádel y Tenis" },
    { id: "torneos", label: "Torneos" },
    { id: "equipos", label: "Equipos de Competición" },
    { id: "ventapalas", label: "Venta de Palas" },
  ];

  const setImg = (id, url) => setContent({ ...content, services_images: { ...content.services_images, [id]: url } });

  return (
    <div className="max-w-4xl space-y-6" data-testid="site-content-tab">
      {/* Campamentos section toggle */}
      <div className="bg-white border rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <Sun className="w-6 h-6 text-orange-500 mt-0.5" />
            <div>
              <h3 className="text-lg font-bold text-slate-900">Sección "Campamento de Verano"</h3>
              <p className="text-xs text-slate-500 mt-1">Activa/desactiva la sección naranja de campamentos en la portada. El contenido se conserva para volver a mostrarlo cuando quieras.</p>
            </div>
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={content.campamentos_enabled} onChange={(e) => setContent({ ...content, campamentos_enabled: e.target.checked })} className="sr-only peer" data-testid="camp-enabled-toggle" />
            <div className="relative w-14 h-7 bg-slate-300 peer-checked:bg-green-600 rounded-full transition-colors">
              <div className={`absolute top-0.5 left-0.5 bg-white w-6 h-6 rounded-full transition-transform ${content.campamentos_enabled ? "translate-x-7" : ""}`}></div>
            </div>
            <span className="ml-2 text-sm font-bold">{content.campamentos_enabled ? "Visible" : "Oculto"}</span>
          </label>
        </div>
      </div>

      {/* Services images */}
      <div className="bg-white border rounded-xl p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-1">Fotos de "Servicios"</h3>
        <p className="text-xs text-slate-500 mb-4">Cambia las 4 fotos que aparecen en la sección Servicios. Sube desde tu ordenador o pega una URL.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {servicesList.map((s) => (
            <div key={s.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50/40">
              <label className="block text-sm font-semibold mb-2">{s.label}</label>
              <FileUpload
                value={content.services_images[s.id] || ""}
                onChange={(url) => setImg(s.id, url)}
                accept="image/*"
                label="Cambiar foto"
              />
              {content.services_images[s.id] && (
                <button
                  onClick={() => setImg(s.id, "")}
                  className="text-xs text-red-600 hover:text-red-800 mt-1"
                  data-testid={`service-img-reset-${s.id}`}
                >
                  Restablecer foto original
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Pricing PDF */}
      <div className="bg-white border rounded-xl p-6">
        <div className="flex items-start gap-3 mb-3">
          <FileText className="w-6 h-6 text-green-600 mt-0.5" />
          <div>
            <h3 className="text-lg font-bold text-slate-900">PDF Listado de precios</h3>
            <p className="text-xs text-slate-500 mt-1">PDF que se descarga desde el botón "LISTADO COMPLETO DE PRECIOS" en la sección de Tarifas.</p>
          </div>
        </div>
        <FileUpload
          value={content.pricing_pdf_url}
          onChange={(url) => setContent({ ...content, pricing_pdf_url: url })}
          accept="application/pdf"
          label="Subir nuevo PDF"
        />
        {content.pricing_pdf_url && (
          <a href={content.pricing_pdf_url} target="_blank" rel="noopener noreferrer"
             className="inline-block text-xs text-green-700 hover:text-green-900 underline mt-2"
             data-testid="pricing-pdf-preview">
            Ver PDF actual →
          </a>
        )}
      </div>

      {msg && <div className={`px-3 py-2 rounded-lg text-sm ${msg.startsWith("✅") ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>{msg}</div>}

      <div className="flex gap-2 sticky bottom-4">
        <button onClick={save} disabled={saving} data-testid="site-content-save"
          className="bg-green-600 text-white px-6 py-3 rounded-full font-bold hover:bg-green-700 disabled:opacity-50 shadow-lg">
          {saving ? "Guardando..." : "Guardar cambios"}
        </button>
        <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-600 hover:text-green-600 px-3 py-2">Ver en la web →</a>
      </div>
    </div>
  );
}

// ========== CAMP CONTENT TAB (Campamento de Verano) ==========
function CampContentTab() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [openSection, setOpenSection] = useState("header");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/camp-content`);
        setContent(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await axios.put(`${API}/admin/camp-content`, content, { headers: authHeaders() });
      setMsg("✅ Cambios guardados. Recarga la web para verlos.");
    } catch (e) {
      setMsg("❌ Error: " + errMsg(e));
    } finally { setSaving(false); }
  };

  if (loading || !content) return <div className="p-8 text-center text-slate-500">Cargando...</div>;

  // Path setters
  const setField = (path, value) => {
    setContent((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) {
        obj[parts[i]] = { ...(obj[parts[i]] || {}) };
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const setList = (path, list) => setField(path, list);
  const addItem = (path, item) => {
    const parts = path.split(".");
    let cur = content;
    for (const p of parts) cur = cur?.[p];
    setList(path, [...(cur || []), item]);
  };
  const removeItem = (path, i) => {
    const parts = path.split(".");
    let cur = content;
    for (const p of parts) cur = cur?.[p];
    setList(path, (cur || []).filter((_, k) => k !== i));
  };
  const moveItem = (path, i, dir) => {
    const parts = path.split(".");
    let cur = content;
    for (const p of parts) cur = cur?.[p];
    const arr = [...(cur || [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setList(path, arr);
  };
  const updateItem = (path, i, key, value) => {
    const parts = path.split(".");
    let cur = content;
    for (const p of parts) cur = cur?.[p];
    const arr = [...(cur || [])];
    arr[i] = typeof arr[i] === "string" ? value : { ...arr[i], [key]: value };
    setList(path, arr);
  };

  const Section = ({ id, title, children, visiblePath, alwaysVisible }) => {
    const open = openSection === id;
    const visible = visiblePath ? (path => {
      const parts = path.split(".");
      let cur = content;
      for (const p of parts) cur = cur?.[p];
      return cur !== false;
    })(visiblePath) : true;
    return (
      <div className="border-2 border-slate-200 rounded-xl overflow-hidden bg-white">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100" onClick={() => setOpenSection(open ? null : id)}>
          <div className="flex items-center gap-2">
            <span className="text-slate-500 text-xs">{open ? "▼" : "▶"}</span>
            <h3 className="font-bold text-slate-900 text-sm">{title}</h3>
            {!alwaysVisible && (
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${visible ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}`}>
                {visible ? "Visible" : "Oculto"}
              </span>
            )}
          </div>
          {!alwaysVisible && visiblePath && (
            <label className="inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
              <input type="checkbox" checked={visible} onChange={(e) => setField(visiblePath, e.target.checked)} className="sr-only peer" />
              <div className="relative w-10 h-5 bg-slate-300 peer-checked:bg-green-600 rounded-full">
                <div className={`absolute top-0.5 left-0.5 bg-white w-4 h-4 rounded-full transition-transform ${visible ? "translate-x-5" : ""}`}></div>
              </div>
            </label>
          )}
        </div>
        {open && <div className="p-4 space-y-3 text-sm">{children}</div>}
      </div>
    );
  };

  const Field = ({ label, value, onChange, type = "text", placeholder = "" }) => (
    <div>
      <label className="block text-xs font-semibold mb-1 text-slate-700">{label}</label>
      {type === "textarea" ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows="2" placeholder={placeholder}
          className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-green-600 text-sm" />
      ) : (
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:border-green-600 text-sm" />
      )}
    </div>
  );

  const ListEditor = ({ path, items, columns, addLabel = "Añadir" }) => (
    <div className="space-y-2">
      {(items || []).map((it, i) => (
        <div key={i} className="grid items-center gap-2 border rounded-lg p-2 bg-slate-50/50" style={{ gridTemplateColumns: `${columns.map(c => c.width || "1fr").join(" ")} auto` }}>
          {columns.map((c) => (
            typeof it === "string" ? (
              <input key={c.key} type="text" value={it} onChange={(e) => updateItem(path, i, c.key, e.target.value)}
                placeholder={c.placeholder}
                className="border rounded px-2 py-1.5 text-sm focus:outline-none focus:border-green-600" />
            ) : (
              <input key={c.key} type="text" value={it[c.key] || ""} onChange={(e) => updateItem(path, i, c.key, e.target.value)}
                placeholder={c.placeholder}
                className="border rounded px-2 py-1.5 text-sm focus:outline-none focus:border-green-600" />
            )
          ))}
          <div className="flex gap-0.5">
            <button onClick={() => moveItem(path, i, -1)} disabled={i === 0} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
            <button onClick={() => moveItem(path, i, 1)} disabled={i === items.length - 1} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
            <button onClick={() => removeItem(path, i)} className="p-1 rounded hover:bg-red-100 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      ))}
      <button onClick={() => addItem(path, columns.length === 1 && columns[0].key === "_str" ? "" : Object.fromEntries(columns.map(c => [c.key, ""])))}
        className="text-xs text-green-700 hover:text-green-900 font-bold inline-flex items-center gap-1">
        <Plus className="w-3.5 h-3.5" /> {addLabel}
      </button>
    </div>
  );

  return (
    <div className="max-w-4xl space-y-3" data-testid="camp-content-tab">
      {/* Master toggle */}
      <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-orange-900">🏖️ Sección "Campamento de Verano"</h3>
          <p className="text-xs text-orange-700 mt-1">Toggle maestro. Si lo desactivas, TODA la sección naranja desaparece de la portada.</p>
        </div>
        <label className="inline-flex items-center cursor-pointer">
          <input type="checkbox" checked={content.enabled !== false} onChange={(e) => setField("enabled", e.target.checked)} className="sr-only peer" data-testid="camp-master-toggle" />
          <div className="relative w-14 h-7 bg-slate-300 peer-checked:bg-green-600 rounded-full">
            <div className={`absolute top-0.5 left-0.5 bg-white w-6 h-6 rounded-full transition-transform ${content.enabled !== false ? "translate-x-7" : ""}`}></div>
          </div>
          <span className="ml-2 text-sm font-bold">{content.enabled !== false ? "Visible" : "Oculto"}</span>
        </label>
      </div>

      {/* Header */}
      <Section id="header" title="1. Cabecera (título + subtítulo)" visiblePath="header.visible">
        <Field label="Etiqueta arriba (badge)" value={content.header?.badge} onChange={(v) => setField("header.badge", v)} placeholder="¡PLAZAS LIMITADAS!" />
        <Field label="Título grande" value={content.header?.title} onChange={(v) => setField("header.title", v)} placeholder="Campamento de Verano 2026" />
        <Field label="Subtítulo (fechas y edades)" value={content.header?.subtitle} onChange={(v) => setField("header.subtitle", v)} />
      </Section>

      {/* Actions */}
      <Section id="actions" title="2. Botones (Inscripciones, WhatsApp, Cartel)" visiblePath="actions.visible">
        <Field label="URL formulario Inscripciones" value={content.actions?.inscription_url} onChange={(v) => setField("actions.inscription_url", v)} type="url" />
        <Field label="Texto botón Inscripciones" value={content.actions?.inscription_label} onChange={(v) => setField("actions.inscription_label", v)} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Teléfono WhatsApp (con prefijo, sin +)" value={content.actions?.whatsapp_phone} onChange={(v) => setField("actions.whatsapp_phone", v)} placeholder="34639713537" />
          <Field label="Texto botón WhatsApp" value={content.actions?.whatsapp_label} onChange={(v) => setField("actions.whatsapp_label", v)} />
        </div>
        <Field label="Mensaje pre-rellenado WhatsApp" value={content.actions?.whatsapp_text} onChange={(v) => setField("actions.whatsapp_text", v)} type="textarea" />
        <Field label="Texto botón Descargar cartel" value={content.actions?.flyer_label} onChange={(v) => setField("actions.flyer_label", v)} />
      </Section>

      {/* Cartel 1 */}
      <Section id="cartel1" title="3. Cartel principal (imagen)" visiblePath="cartel1_visible">
        <label className="block text-xs font-semibold mb-1">Imagen del cartel principal</label>
        <FileUpload value={content.cartel1_url} onChange={(url) => setField("cartel1_url", url)} accept="image/*" label="Cambiar cartel" />
      </Section>

      {/* Cartel 2 */}
      <Section id="cartel2" title='4. Cartel expandible ("Ver más info")' visiblePath="cartel2_visible">
        <label className="block text-xs font-semibold mb-1">Imagen que aparece al pulsar "Ver más información"</label>
        <FileUpload value={content.cartel2_url} onChange={(url) => setField("cartel2_url", url)} accept="image/*" label="Cambiar cartel" />
      </Section>

      {/* Extras */}
      <Section id="extras" title="5. Opciones Extras" visiblePath="extras.visible">
        <Field label="Título" value={content.extras?.title} onChange={(v) => setField("extras.title", v)} />
        <ListEditor path="extras.items" items={content.extras?.items}
          columns={[{ key: "name", placeholder: "Nombre extra", width: "2fr" }, { key: "price", placeholder: "Precio", width: "1fr" }]}
          addLabel="Añadir extra" />
      </Section>

      {/* Courses */}
      <Section id="courses" title="6. Elige tu curso" visiblePath="courses.visible">
        <Field label="Título" value={content.courses?.title} onChange={(v) => setField("courses.title", v)} />
        <ListEditor path="courses.items" items={content.courses?.items}
          columns={[
            { key: "name", placeholder: "Ej: Combinado", width: "1fr" },
            { key: "subtitle", placeholder: "Ej: Golf + Pádel + Tenis", width: "1.5fr" },
            { key: "color", placeholder: "green/amber/blue/purple/red", width: "1fr" },
          ]}
          addLabel="Añadir curso" />
      </Section>

      {/* Included */}
      <Section id="included" title="7. ¿Qué incluye?" visiblePath="included.visible">
        <Field label="Título" value={content.included?.title} onChange={(v) => setField("included.title", v)} />
        <ListEditor path="included.items" items={content.included?.items}
          columns={[{ key: "_str", placeholder: "Ej: Autobús de IDA desde Mérida", width: "1fr" }]}
          addLabel="Añadir línea" />
      </Section>

      {/* Prices */}
      <Section id="prices" title="8. Precios" visiblePath="prices.visible">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Título" value={content.prices?.title} onChange={(v) => setField("prices.title", v)} />
          <Field label="Columna Abonados" value={content.prices?.col_member} onChange={(v) => setField("prices.col_member", v)} />
          <Field label="Columna No abonados" value={content.prices?.col_non_member} onChange={(v) => setField("prices.col_non_member", v)} />
        </div>
        <ListEditor path="prices.rows" items={content.prices?.rows}
          columns={[
            { key: "weeks", placeholder: "1 semana", width: "1.2fr" },
            { key: "member", placeholder: "89€", width: "1fr" },
            { key: "non_member", placeholder: "109€", width: "1fr" },
          ]}
          addLabel="Añadir fila" />
        <Field label="Nota bajo la tabla" value={content.prices?.note} onChange={(v) => setField("prices.note", v)} />
      </Section>

      {/* Bus stops */}
      <Section id="bus" title="9. Horarios del Autobús" visiblePath="bus_stops.visible">
        <Field label="Título" value={content.bus_stops?.title} onChange={(v) => setField("bus_stops.title", v)} />
        <Field label="Subtítulo" value={content.bus_stops?.subtitle} onChange={(v) => setField("bus_stops.subtitle", v)} />
        <ListEditor path="bus_stops.items" items={content.bus_stops?.items}
          columns={[
            { key: "ida", placeholder: "9:20h", width: "1fr" },
            { key: "vuelta", placeholder: "15:30h", width: "1fr" },
            { key: "place", placeholder: "Cervecería El Faro", width: "3fr" },
          ]}
          addLabel="Añadir parada" />
      </Section>

      {/* Recommendations */}
      <Section id="recs" title="10. Recomendaciones (mochila)" visiblePath="recommendations.visible">
        <Field label="Título" value={content.recommendations?.title} onChange={(v) => setField("recommendations.title", v)} />
        <Field label="Introducción" value={content.recommendations?.intro} onChange={(v) => setField("recommendations.intro", v)} />
        <ListEditor path="recommendations.items" items={content.recommendations?.items}
          columns={[{ key: "_str", placeholder: "Ej: Gorra", width: "1fr" }]}
          addLabel="Añadir ítem" />
        <Field label="Nota final" value={content.recommendations?.footer_note} onChange={(v) => setField("recommendations.footer_note", v)} type="textarea" />
      </Section>

      {/* CTA */}
      <Section id="cta" title="11. CTA + Teléfonos" visiblePath="cta.visible">
        <Field label="Título" value={content.cta?.title} onChange={(v) => setField("cta.title", v)} />
        <Field label="Subtítulo" value={content.cta?.subtitle} onChange={(v) => setField("cta.subtitle", v)} />
        <Field label="Etiqueta antes de los teléfonos" value={content.cta?.phones_label} onChange={(v) => setField("cta.phones_label", v)} />
        <ListEditor path="cta.phones" items={content.cta?.phones}
          columns={[
            { key: "number", placeholder: "639 71 35 37", width: "1fr" },
            { key: "tel", placeholder: "639713537 (sin espacios)", width: "1fr" },
          ]}
          addLabel="Añadir teléfono" />
      </Section>

      {msg && <div className={`px-3 py-2 rounded-lg text-sm ${msg.startsWith("✅") ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>{msg}</div>}

      <div className="flex gap-2 sticky bottom-4 pt-2">
        <button onClick={save} disabled={saving} data-testid="camp-content-save"
          className="bg-green-600 text-white px-6 py-3 rounded-full font-bold hover:bg-green-700 disabled:opacity-50 shadow-lg">
          {saving ? "Guardando..." : "Guardar cambios"}
        </button>
        <a href="/#campamentos" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-600 hover:text-green-600 px-3 py-2">Ver en la web →</a>
      </div>
    </div>
  );
}

function AdminDashboard({ email, onLogout }) {
  const [tab, setTab] = useState("products");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-black text-slate-900">Panel Admin · Don Tello</h1>
            <p className="text-xs text-slate-500">{email}</p>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="text-sm text-slate-600 hover:text-green-600 px-3 py-2">Ver web</a>
            <button onClick={onLogout} data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-full text-sm font-bold">
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-t">
          <nav className="flex gap-1">
            <button onClick={() => setTab("products")} data-testid="tab-products"
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 ${tab === "products" ? "border-green-600 text-green-700" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
              <Package className="w-4 h-4" /> Productos
            </button>
            <button onClick={() => setTab("orders")} data-testid="tab-orders"
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 ${tab === "orders" ? "border-green-600 text-green-700" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
              <ShoppingBag className="w-4 h-4" /> Pedidos
            </button>
            <button onClick={() => setTab("promo")} data-testid="tab-promo"
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 ${tab === "promo" ? "border-green-600 text-green-700" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
              <Megaphone className="w-4 h-4" /> Banner Promocional
            </button>
            <button onClick={() => setTab("content")} data-testid="tab-content"
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 ${tab === "content" ? "border-green-600 text-green-700" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
              <Settings className="w-4 h-4" /> Contenido web
            </button>
            <button onClick={() => setTab("camp")} data-testid="tab-camp"
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 ${tab === "camp" ? "border-green-600 text-green-700" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
              <Sun className="w-4 h-4" /> Campamento
            </button>
          </nav>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {tab === "products" && <ProductsTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "promo" && <PromoBannerTab />}
        {tab === "content" && <SiteContentTab />}
        {tab === "camp" && <CampContentTab />}
      </main>
    </div>
  );
}

// ========== ROOT ==========
export default function AdminApp() {
  const [email, setEmail] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      if (!getToken()) { setChecking(false); return; }
      try {
        const { data } = await axios.get(`${API}/admin/me`, { headers: authHeaders() });
        setEmail(data.email);
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setEmail(null);
  };

  if (checking) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div></div>;
  }

  if (!email) return <AdminLogin onLogin={setEmail} />;
  return <AdminDashboard email={email} onLogout={logout} />;
}
